import {
  FileId,
  TprmExternalQuestionnaireExternal,
  TprmExternalQuestionnaireId,
  TprmQuestionTemplateId,
  File as FileT,
  TprmQuestionnaireTemplate,
  AccountMin,
  ThirdPartyId,
  DataRoomId,
  TprmExternalQuestionnaireMin,
  PortalDataRoomOutstanding,
  PortalInviteInfoExternal,
  PortalUserMin,
  Portal,
  InvitePortalUser,
} from "../../Types";
import {invalidateQueries, q} from "../../state";
import {uploadImpl} from "../files";
import {DEFAULT_API_OPTIONS} from "../jsonApi";
import jsonApi, {API_BASE} from "./jsonApi";

export async function getLegacyQuestionnaireThirdPartyId(
  account: AccountMin,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
) {
  return await jsonApi.get<ThirdPartyId>(
    `/region/${account.region}/external_api/accounts/${account.account_id}/portal/legacy_questionnaire/${externalQuestionnaireId}/third_party_id`,
  );
}

export async function getExternalQuestionnaire(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
) {
  return await jsonApi.get<TprmExternalQuestionnaireExternal>(
    `/region/${account.region}/external_api/accounts/${account.account_id}/portal/${thirdPartyId}/questionnaire/${externalQuestionnaireId}`,
  );
}

export async function getExternalQuestionnaireTemplate(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
) {
  return await jsonApi.get<TprmQuestionnaireTemplate>(
    `/region/${account.region}/external_api/accounts/${account.account_id}/portal/${thirdPartyId}/questionnaire/${externalQuestionnaireId}/template`,
  );
}

export async function updateTextAnswer(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  text: string,
) {
  const res = await jsonApi.post<"ok">(
    `/region/${account.region}/external_api/accounts/${account.account_id}/portal/${thirdPartyId}/questionnaire/${externalQuestionnaireId}/question/${questionTemplateId}/update_text`,
    text,
  );

  await invalidateQueries([q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId)]);
  return res;
}

export async function updateSelectAnswer(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  values: string[],
) {
  const res = await jsonApi.post<"ok">(
    `/region/${account.region}/external_api/accounts/${account.account_id}/portal/${thirdPartyId}/questionnaire/${externalQuestionnaireId}/question/${questionTemplateId}/update_select`,
    values,
  );

  await invalidateQueries([q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId)]);

  return res;
}

export async function addFilesToAnswer(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  fileIds: FileId[],
) {
  const res = await jsonApi.post<"ok">(
    `/region/${account.region}/external_api/accounts/${account.account_id}/portal/${thirdPartyId}/questionnaire/${externalQuestionnaireId}/question/${questionTemplateId}/add_files`,
    fileIds,
  );

  await invalidateQueries([q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId)]);

  return res;
}

export async function removeFileFromAnswer(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  fileId: FileId,
) {
  const res = await jsonApi.post<"ok">(
    `/region/${account.region}/external_api/accounts/${account.account_id}/portal/${thirdPartyId}/questionnaire/${externalQuestionnaireId}/question/${questionTemplateId}/remove_file`,
    fileId,
  );

  await invalidateQueries([q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId)]);

  return res;
}

export async function addSubQuestionnaireToAnswer(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
) {
  const res = await jsonApi.post<TprmExternalQuestionnaireId>(
    `/region/${account.region}/external_api/accounts/${account.account_id}/portal/${thirdPartyId}/questionnaire/${externalQuestionnaireId}/question/${questionTemplateId}/add_sub_questionnaire`,
  );

  await invalidateQueries([q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId)]);

  return res;
}

export async function removeSubQuestionnaireFromAnswer(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  rootQuestionnaireId: TprmExternalQuestionnaireId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  subExternalQuestionnaireId: TprmExternalQuestionnaireId,
) {
  const res = await jsonApi.post<"ok">(
    `/region/${account.region}/external_api/accounts/${account.account_id}/portal/${thirdPartyId}/questionnaire/${externalQuestionnaireId}/question/${questionTemplateId}/remove_sub_questionnaire`,
    subExternalQuestionnaireId,
  );

  await invalidateQueries([q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, rootQuestionnaireId)]);

  return res;
}

export function uploadFile(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  file: File,
  progressFn?: (progress: number) => void,
  signal?: AbortSignal,
): Promise<FileT> {
  return uploadImpl(
    `${API_BASE}/region/${account.region}/external_api/accounts/${account.account_id}/portal/${thirdPartyId}/questionnaire/${externalQuestionnaireId}/files`,
    file,
    progressFn,
    signal,
  );
}

export function downloadFile(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  fileId: FileId,
) {
  jsonApi.download(
    `/region/${account.region}/external_api/accounts/${account.account_id}/portal/${thirdPartyId}/questionnaire/${externalQuestionnaireId}/files/${fileId}`,
  );
}

export async function submitExternalQuestionnaire(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
) {
  const res = await jsonApi.post<"ok">(
    `/region/${account.region}/external_api/accounts/${account.account_id}/portal/${thirdPartyId}/questionnaire/${externalQuestionnaireId}/submit`,
  );
  await invalidateQueries([
    q.external.portal.externalQuestionnaire.filter(account, thirdPartyId, externalQuestionnaireId),
  ]);
  return res;
}

export async function acceptInviteKey(account: AccountMin, thirdPartyId: ThirdPartyId, inviteKey: string) {
  return await jsonApi.post<"ok">(
    `/region/${account.region}/external_api/accounts/${account.account_id}/portal/${thirdPartyId}/accept_invite_key`,
    inviteKey,
  );
}

export async function validateInvite(account: AccountMin, thirdPartyId: ThirdPartyId, token: string) {
  return await jsonApi.get<PortalInviteInfoExternal>(
    `/region/${account.region}/external_api/accounts/${account.account_id}/portal/${thirdPartyId}/validate_invite`,
    {
      ...DEFAULT_API_OPTIONS,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
}

export async function listDataRooms(account: AccountMin, thirdPartyId: ThirdPartyId) {
  return await jsonApi.get<PortalDataRoomOutstanding[]>(
    `/region/${account.region}/external_api/accounts/${account.account_id}/portal/${thirdPartyId}/data_rooms`,
  );
}

export async function listExternalQuestionnaires(account: AccountMin, dataRoomId: DataRoomId) {
  return await jsonApi.get<TprmExternalQuestionnaireMin[]>(
    `/region/${account.region}/external_api/accounts/${account.account_id}/portal/${dataRoomId}/external_questionnaires`,
  );
}

export async function listPortalUsers(account: AccountMin, thirdPartyId: ThirdPartyId) {
  return await jsonApi.get<PortalUserMin[]>(
    `/region/${account.region}/external_api/accounts/${account.account_id}/portal/${thirdPartyId}/portal_users`,
  );
}

export async function getPortal(account: AccountMin, thirdPartyId: ThirdPartyId) {
  return await jsonApi.get<Portal>(
    `/region/${account.region}/external_api/accounts/${account.account_id}/portal/${thirdPartyId}`,
  );
}

export async function invitePortalUser(
  account: AccountMin,
  thirdPartyId: ThirdPartyId,
  payload: InvitePortalUser[],
): Promise<"ok"> {
  const res = await jsonApi.post<"ok">(
    `/region/${account.region}/external_api/accounts/${account.account_id}/portal/${thirdPartyId}/invite_user`,
    payload,
  );
  await invalidateQueries([q.external.portal.portalUsers.filter()]);
  return res;
}
